/*
 * Lista de cotizaciones.
 *
 * Lista de cotizaciones paginada. Muestra la opción para poder crear
 * un nuevo registro de cotización (vendedor). Los filtros en este lista son: Estatus,
 * Fecha de creación, Ordenamiento, Clientes y Creadores, así como un buscador por palabras general.
 *
 * Created on Fri Jun 16 2023 by José Luis Chávez Ayala.
 *
 * Copyright (c) 2023 Turn My App.
 */

<template>
  <div>

    <!-- #region::Searcher, filters and actions buttons -->
    <QuotesListFilters
      :user-detail="userDetail"
      :type-user="typeUser"
      title="Cotizaciones"
      new-item-button-label="Nueva cotización"
      searcher-placeholder="Buscar cotización"
      :filter-route="filterRoute"
      :can-apply-global-filters="canApplyGlobalFilters"
      @on:filter="handleFilter"
      @on:click="$router.push({ name: 'new-quote' })"
    />
    <!-- #endregion::Searcher, filters and actions buttons -->

    <!-- #region::Skeleton -->
    <template v-if="isLoadingQuotes">
      <b-skeleton
        class="mt-1"
        animation="wave"
        width="100%"
        height="430px"
      />
    </template>
    <!-- #endregion::Skeleton -->

    <template v-else>
      <b-card
        class="mt-1 border border-dark shadow-none"
        no-body
      >
        <b-card-text>

          <!-- #region::Quotes list table -->
          <b-table
            v-if="availableQuotes"
            :items="quotes"
            :fields="columns"
            responsive
            class="my-0"
          >
            <!-- #region:Virtual column for index -->
            <template #cell(No)="data">
              {{ indexFrom + data.index }}
            </template>
            <!-- #endregion:Virtual column for index -->

            <!-- #region:Virtual column for ID -->
            <template #cell(ID)="{ item }">
              <router-link :to="{ name: 'quote-details', params: { id: item.IdQuote } }">
                {{ item.ID }}
              </router-link>
            </template>
            <!-- #endregion:Virtual column for ID -->

            <!-- #region::Virtual column for created at -->
            <template #cell(created_at)="{ item }">
              <span class="text-nowrap">{{ item.created_at | moment("DD/MM/YYYY hh:mm a") }}</span>
            </template>
            <!-- #endregion::Virtual column for created at -->

            <!-- #region:Virtual column for quote creator -->
            <template #cell(NameUser)="{ item }">
              <span class="text-nowrap">{{ item.NameUser }}</span>
            </template>
            <!-- #endregion:Virtual column for quote creator -->

            <!-- #region:Virtual column for index -->
            <template #cell(client)="{ item }">
              <span class="text-nowrap">{{ `${item.NameClient} ${item.LastNameClient}` }}</span>
            </template>
            <!-- #endregion:Virtual column for index -->

            <!-- #region::Virtual column for products count -->
            <template #cell(Qty)="{ item }">
              <span class="text-nowrap">{{ item.Qty | placeholder('Sin especificar') }}</span>
            </template>
            <!-- #endregion::Virtual column for products count -->

            <!-- #region::Virtual column for products count -->
            <template #cell(Invoice)="{ item }">
              <span class="text-nowrap">{{ item.Invoice | placeholder('Sin especificar') }}</span>
            </template>
            <!-- #endregion::Virtual column for products count -->

            <!-- #region::Virtual column for subtotal usd -->
            <template #cell(SubtotalUsd)="{ item }">
              <span v-if=" item.SubtotalUsd">
                {{ item.SubtotalUsd | currency }}
              </span>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for subtotal usd -->

            <!-- #region::Virtual column for exchange rate -->
            <template #cell(ExchangeRate)="{ item }">
              <span v-if=" item.ExchangeRate">
                {{ item.ExchangeRate | currency }}
              </span>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for exchange rate -->

            <!-- #region::Virtual column for subtotal mxn -->
            <template #cell(TotalMxn)="{ item }">
              <span v-if=" item.TotalMxn">
                {{ item.TotalMxn | currency }}
              </span>
              <span
                v-else
                class="text-nowrap"
              >Sin especificar</span>
            </template>
            <!-- #endregion::Virtual column for subtotal mxn -->

            <!-- #region::Virtual column for status -->
            <template #cell(StatusText)="{ item }">
              <h6><b-badge
                class="ava-label"
                :variant="getStatusStyle(item.StatusText)"
              >
                {{ item.StatusText }}
              </b-badge>
              </h6>
            </template>
            <!-- #endregion::Virtual column for status -->

          </b-table>
          <!-- #endregion::Quotes list table -->

          <!-- #region::Alert when no quotes are available -->
          <b-alert
            v-if="!availableQuotes && !isLoadingQuotes"
            variant="warning"
            class="my-0"
            show
          >
            <div class="alert-body">
              <span>No se encontraron cotizaciones.</span>
            </div>
          </b-alert>
          <!-- #endregion::Alert when no quotes are available -->

        </b-card-text>
      </b-card>
    </template>

    <!-- #region begin::Pagination & items per list -->
    <BasicPaginator
      v-if="availableQuotes"
      ref="basic-paginator"
      class="mt-2"
      :total-rows="totalQuotes"
      :callback="handleChangePagination"
      :can-apply-global-pagination="canApplyGlobalPagination"
    />
    <!-- #endregion end::Pagination & items per list -->

  </div>
</template>

<script>
// #region Imports
import { mapGetters, mapActions } from 'vuex'
import {
  BSkeleton, BCard, BCardText, BTable, BAlert, BBadge,
} from 'bootstrap-vue'
import BasicPaginator from '@/components/tables/BasicPaginator.vue'
import QuotesListFilters from '@/modules/trade/quotes/components/QuotesListFilters.vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import getError from '@/helpers/ErrorsHandler'
// #endregion

export default {
  components: {
    BCard,
    BAlert,
    BBadge,
    BTable,
    BCardText,
    BSkeleton,
    BasicPaginator,
    QuotesListFilters,
  },
  props: {
    /**
     * Contiene los detalles del usuario cliente
     */
    userDetail: {
      type: Boolean,
      default: false,
    },
    /**
     * Tipo de usuario asociado a la cotización
     */
    typeUser: {
      type: String,
      default: 'cliente',
    },
    /**
     * Representa un identificador externo de usuario
     */
    idUserExternal: {
      type: String,
      default: null,
    },
    /**
     * Representa un identificador externo de cliente
     */
    idClientExternal: {
      type: String,
      default: null,
    },
    /**
     * Indica si se pueden aplicar los filtros globales
     */
    canApplyGlobalFilters: {
      type: Boolean,
      default: true,
    },
    /**
     * Indica si se puede aplicar la paginación global
     */
    canApplyGlobalPagination: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      lastPage: 0,
      indexFrom: 0,
      totalQuotes: 0,
      filterRoute: {},
      routeParmas: '',
      columns: [
        {
          key: 'No',
          label: 'No.',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'ID',
          label: 'ID',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'created_at',
          label: 'Creada',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'NameUser',
          label: 'Creada por',
          thStyle: 'text-transform: none;',
          thClass: (!this.$ability.can('create', 'Quote') && this.typeUser === 'cliente') ? 'bg-light' : 'd-none',
          tdClass: (!this.$ability.can('create', 'Quote') && this.typeUser === 'cliente') ? '' : 'd-none',
        },
        {
          key: 'client',
          label: 'Cliente',
          thStyle: 'text-transform: none;',
          thClass: (this.userDetail && this.typeUser === 'cliente') ? 'd-none' : 'bg-light',
          tdClass: (this.userDetail && this.typeUser === 'cliente') ? 'd-none' : '',
        },
        {
          key: 'Qty',
          label: 'Productos',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'Invoice',
          label: 'Solicitud factura',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light text-nowrap',
        },
        {
          key: 'SubtotalUsd',
          label: 'Subtotal USD',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light text-nowrap',
        },
        {
          key: 'ExchangeRate',
          label: 'T.C.',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
        {
          key: 'TotalMxn',
          label: 'Total MXN',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light text-nowrap',
        },
        {
          key: 'StatusText',
          label: 'Estatus',
          thStyle: 'text-transform: none;',
          thClass: 'bg-light',
        },
      ],
      quotes: [],
      isLoadingQuotes: false,

      /**
       * Local filters
       */
      localSortBy: null,
      localStatus: null,
      localCreator: null,
      localCustomer: null,
      localSearchTerm: null,
      localCreatedAtDate: null,

      /**
       * Local pagination
       */
      localPerPage: 10,
      localCurrentPage: 1,
    }
  },
  computed: {
    ...mapGetters({
      /**
       * Filtros globales
       */
      getFilteredSortBy: 'filters/getFilteredSortBy',
      getFilteredStatus: 'filters/getFilteredStatus',
      getFilteredCreator: 'filters/getFilteredCreator',
      getFilteredPerPage: 'filters/getFilteredPerPage',
      getFilteredCustomer: 'filters/getFilteredCustomer',
      getFilteredSearchTerm: 'filters/getFilteredSearchTerm',
      getFilteredCurrentPage: 'filters/getFilteredCurrentPage',
      getFilteredCreatedAtDate: 'filters/getFilteredCreatedAtDate',
    }),
    /**
     * Ordenamiento global (filtro en store)
     */
    filteredSortBy: {
      get() { return this.getFilteredSortBy },
      set(value) { this.setFilteredSortBy(value) },
    },
    /**
     * Filtro de estatus (filtro en store)
     */
    filteredStatus: {
      get() { return this.getFilteredStatus },
      set(value) { this.setFilteredStatus(value) },
    },
    /**
     * Filtro de creador (filtro en store)
     */
    filteredCreator: {
      get() { return this.getFilteredCreator },
      set(value) { this.setFilteredCreator(value) },
    },
    /**
     * Filtro de cliente (filtro en store)
     */
    filteredCustomer: {
      get() { return this.getFilteredCustomer },
      set(value) { this.setFilteredCustomer(value) },
    },
    /**
     * Indica la cantidad de elementos por página (paginado global)
     */
    filteredPerPage: {
      get() { return this.getFilteredPerPage },
      set(value) { this.setFilteredPerPage(value) },
    },
    /**
     * Término de búsqueda filtrado (filtro en store)
     */
    filteredSearchTerm: {
      get() { return this.getFilteredSearchTerm },
      set(value) { this.setFilteredSearchTerm(value) },
    },
    /**
     * Indica la página actual (paginado global)
     */
    filteredCurrentPage: {
      get() { return this.getFilteredCurrentPage },
      set(value) { this.setFilteredCurrentPage(value) },
    },
    /**
     * Filtro de fecha de creación (filtro en store)
     */
    filteredCreatedAtDate: {
      get() { return this.getFilteredCreatedAtDate },
      set(value) { this.setFilteredCreatedAtDate(value) },
    },
    availableQuotes() {
      return this.totalQuotes > 0
    },
  },

  /**
  * Hook que se ejecuta cuando el componente es montado
  *
  * @summary Verifica si hay parámetros en la ruta para cargar el filtro
  * proporcionados por el componente de dashboard. De lo contrario
  * carga la lista de cotizaciones y los filtros
  * @exception ⁡⁢⁣⁢Error de validación en la carga de registros de órdenes⁡
  */
  async mounted() {
    try {
      if (this.routeParmas) {
        this.filterRoute = { text: this.routeParmas }
        this.listenerParmasDashboard('')
      } else {
        await this.loadQuotesListWithVerification()
      }
    } catch (error) {
      this.$swal.close()
      this.showToast('Error de validación', getError(error), 'danger')
    }
  },

  /**
  * Hook que se ejecuta cuando se crea el componente
  *
  * @summary Establece el filtro cargado en la ruta originario del dashboard
  */
  async created() {
    this.routeParmas = this.$store.state.dashboard.paramsDashboard
  },
  methods: {
    ...mapActions({
      loadQuotes: 'quotes/loadQuotes',
      loadClientsSuggests: 'quotes/loadClientsSuggests',
      loadCreatorsSuggests: 'quotes/loadCreatorsSuggests',
      listenerParmasDashboard: 'dashboard/listenerParmasDashboard',

      /**
       * Filtros globales
       */
      setFilteredSortBy: 'filters/setFilteredSortBy',
      setFilteredStatus: 'filters/setFilteredStatus',
      setFilteredCreator: 'filters/setFilteredCreator',
      setFilteredCustomer: 'filters/setFilteredCustomer',
      setFilteredPerPage: 'filters/setFilteredPerPage',
      setFilteredSearchTerm: 'filters/setFilteredSearchTerm',
      setFilteredCurrentPage: 'filters/setFilteredCurrentPage',
      setFilteredCreatedAtDate: 'filters/setFilteredCreatedAtDate',
    }),

    /**
    * Evento de filtrado
    *
    * @summary Atiende el evento de filtrado del compontente filter, asignando
    * términos de búsqueda en base a los campos de selección y el buscador así como
    * establecer los filtros globales en el store
    * @param {Object} filter - Objeto con el tipo de filtro y el valor
    */
    async handleFilter(filter) {
      switch (filter.type) {
        case 'date':
          if (this.canApplyGlobalFilters) {
            this.filteredCreatedAtDate = filter.value
          } else {
            this.localCreatedAtDate = filter.value
          }
          break
        case 'status':
          if (this.canApplyGlobalFilters) {
            this.filteredStatus = filter.value
          } else {
            this.localStatus = filter.value
          }
          break
        case 'sort':
          if (this.canApplyGlobalFilters) {
            this.filteredSortBy = filter.value
          } else {
            this.localSortBy = filter.value
          }
          break
        case 'client':
          if (this.canApplyGlobalFilters) {
            this.filteredCustomer = filter.value
          } else {
            this.localCustomer = filter.value
          }
          break
        case 'creator':
          if (this.canApplyGlobalFilters) {
            this.filteredCreator = filter.value
          } else {
            this.localCreator = filter.value
          }
          break
        default:
          if (this.canApplyGlobalFilters) {
            this.filteredSearchTerm = filter.value
            this.filteredCurrentPage = 1
          } else {
            this.localSearchTerm = filter.value
            this.localCurrentPage = 1
          }
          // eslint-disable-next-line no-unused-expressions
          this.$refs['basic-paginator']?.resetCurrentPage()
          break
      }

      this.loadQuotesListWithVerification()
    },

    /**
    * Carga de elementos de la lista con verificación de paginado
    *
    * @summary Carga la lista de elementos y verifica que la página actual contenga elementos
    */
    async loadQuotesListWithVerification() {
      await this.loadQuotesList()
      await this.verifyContenList()
      await this.loadFilterData()
    },

    /**
    * Evento de paginación
    *
    * @summary Evento del componente de paginación. Establece la página actual y la cantidad
    * de registros por página
    * @param {number} currentPage - Página actual
    * @param {number} perPage - Elementos a mostrar por página
    */
    async handleChangePagination(currentPage, perPage) {
      if (this.canApplyGlobalPagination) {
        this.filteredPerPage = perPage
        this.filteredCurrentPage = currentPage
      } else {
        this.localPerPage = perPage
        this.localCurrentPage = currentPage
      }
      this.loadQuotesList()
    },

    /**
    * Verificación de contenido
    *
    * @summary Verifica que la página actual tenga registros, en caso de no tenerlos
    * tomará la última página disponible y cargará los registros
    */
    async verifyContenList() {
      if (this.quotes.length === 0) {
        if (this.canApplyGlobalPagination) {
          this.filteredCurrentPage = this.lastPage
        } else {
          this.localCurrentPage = this.lastPage
        }

        // Verifica si hay registros disponibles en el servidor
        if (this.availableQuotes) {
          this.$refs['basic-paginator'].resetCurrentPage(this.lastPage)
          await this.loadQuotesList()
        }
      }
    },

    /**
    * Carga de registros de tipo cotizaciones
    *
    * @summary ⁡⁢⁣⁡⁢⁢⁡⁣⁣⁡⁣⁢⁣⁡⁣⁢⁢⁡⁢⁢⁢‍Carga y establece los registros de cotizaciones y los parámetros de paginación⁡⁡⁡⁡⁡⁡
    * @exception ⁡⁢⁣⁢Error de validación en la carga de registros de cotizaciones⁡
    */
    async loadQuotesList() {
      try {
        this.$swal({
          title: 'Cargando...',
          allowOutsideClick: false,
        })
        this.$swal.showLoading()
        this.isLoadingQuotes = true

        const response = await this.loadQuotes(this.loadParams())
        this.quotes = response.data.data.data
        this.totalQuotes = response.data.data.total
        this.indexFrom = response.data.data.from
        this.lastPage = response.data.data.last_page

        this.isLoadingQuotes = false
        this.$swal.close()
      } catch (error) {
        this.$swal.close()
        this.showToast('Error de validación', getError(error), 'danger')
      }
    },

    /**
    * Información de filtros
    *
    * @summary Carga la información de los filtros de cotizaciones
    * @exception ⁡⁢⁣⁢Error de validación en la carga de registros de filtros de cotizaciones ⁡
    */
    async loadFilterData() {
      try {
        await this.loadClientsSuggests()
        await this.loadCreatorsSuggests()
      } catch (error) {
        this.showToast('Error de validación', getError(error), 'danger')
      }
    },

    /**
    * Parámetros de carga
    *
    * @summary Devuelve los parámetros de carga de la lista
    */
    loadParams() {
      return {
        idFilterUser: this.idUserExternal,
        perPage: this.canApplyGlobalPagination ? this.filteredPerPage : this.localPerPage,
        currentPage: this.canApplyGlobalPagination ? this.filteredCurrentPage : this.localCurrentPage,

        /**
         * Verifica que se puedan cargar los filtros del store, si no es así se cargan
         * los filtros locales
         */
        sortTerm: this.canApplyGlobalFilters ? this.filteredSortBy : this.localSortBy,
        statusTerm: this.canApplyGlobalFilters ? this.filteredStatus : this.localStatus,
        searchTerm: this.canApplyGlobalFilters ? this.filteredSearchTerm : this.localSearchTerm,
        idCreatorTerm: this.canApplyGlobalFilters ? this.filteredCreator?.id : this.localCreator?.id,
        dateTerm: this.canApplyGlobalFilters ? this.filteredCreatedAtDate : this.localCreatedAtDate,
        idClientTerm: this.idClientExternal ?? (this.canApplyGlobalFilters ? this.filteredCustomer?.id : this.localCustomer?.id),
      }
    },

    /**
    * Establece el color de banner para el estado de la cotización
    *
    * @param {string} status - Estado de la cotización
    */
    getStatusStyle(status) {
      switch (status) {
        case 'Cotización':
          return 'light-primary'
        case 'Enviado':
          return 'light-warning'
        case 'Solicitud':
          return 'light-success'
        case 'Orden':
          return 'light-info'
        case 'Rechazado':
          return 'light-danger'
        default:
          return 'light-secondary'
      }
    },

    /**
    * Muestra un mensaje de tipo toast
    *
    * @param {string} title - Título del mensaje
    * @param {string} text - Contenido del mensaje
    * @param {string} variant - Tipo del mensaje (warning, success, danger)
    */
    showToast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, variant, icon: 'BellIcon',
        },
      })
    },
  },
}
</script>
